import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Bell,
  MessageCircle,
  ClipboardCheck,
  Activity,
  AlertTriangle,
  Check,
} from "lucide-react";
import serverUrl from "../utils/utilsser";
import { useNavigate } from "react-router-dom";

const BRAND = {
  gradient: "linear-gradient(90deg, #ef5da8 0%, #8b7cf6 100%)",
  teal: "#34d9b8",
  pink: "#ef5da8",
  purple: "#8b7cf6",
};

// Icon + color per notification type — extend as you add activity types
const TYPE_META = {
  chat: { icon: MessageCircle, color: "#34d9b8" },
  test: { icon: ClipboardCheck, color: "#8b7cf6" },
  alert: { icon: AlertTriangle, color: "#ef5da8" },
  activity: { icon: Activity, color: "#f59e0b" },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const API_BASE = `${serverUrl}api/v1/notifications`; // Adjust to your base route path or full domain URL

async function fetchNotifications(code) {
  if (!code) return { items: [], unreadCount: 0 };
  try {
    const res = await fetch(`${API_BASE}/${code}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error);

    // Map _id to id for the frontend
    const items = (data?.data?.items || []).map((item) => ({
      ...item,
      id: item._id || item.id,
    }));

    return { items, unreadCount: data.unreadCount };
  } catch (err) {
    console.error("Error fetching notifications:", err);
    return { items: [], unreadCount: 0 };
  }
}

async function markAsReadApi(id, code) {
  try {
    await fetch(`${API_BASE}/${id}/read/${code}`, { method: "POST" });
    return true;
  } catch (err) {
    console.error("Error marking read:", err);
    return false;
  }
}

async function markAllAsReadApi(code) {
  try {
    await fetch(`${API_BASE}/read-all/${code}`, { method: "POST" });
    return true;
  } catch (err) {
    console.error("Error marking all read:", err);
    return false;
  }
}
const notificationSound = new Audio("/sounds/notification.mp3");
const playNotificationSound = async () => {
  try {
    notificationSound.currentTime = 0;
    await notificationSound.play();
  } catch (error) {
    console.log("Could not play notification sound:", error);
  }
};
// -------------------------------------------------------------------------

const NotificationBell = (referral) => {
  const navigate = useNavigate();
  const code = typeof referral === "object" ? referral?.code : referral || "";
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const load = useCallback(async () => {
    if (!code) return;
    setLoading(true);
    const { items } = await fetchNotifications(code);
    setNotifications(items);
    setLoading(false);
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!code) return;

    const streamUrl = `${serverUrl}api/v1/notifications/stream/${code}`;

    const eventSource = new EventSource(streamUrl);

    eventSource.addEventListener("notification", (event) => {
      const notification = JSON.parse(event.data);

      const item = {
        ...notification,
        id: notification._id || notification.id,
      };
      console.log(item);
      if (document.visibilityState === "visible") {
        console.log("done");
        playNotificationSound();
      }

      setNotifications((prev) => {
        // Prevent duplicate notification
        if (prev.some((n) => n.id === item.id)) {
          return prev;
        }

        return [item, ...prev];
      });
    });

    eventSource.onerror = (error) => {
      console.error("Notification SSE error:", error);
    };

    return () => {
      eventSource.close();
    };
  }, [code]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !btnRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const handleItemClick = async (id) => {
    const notification = notifications.find((n) => n.id === id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    markAsReadApi(id, code);

    if (notification?.redirectTo) {
      const redirectTo = notification.redirectTo;

      if (
        redirectTo.startsWith("http://") ||
        redirectTo.startsWith("https://") ||
        redirectTo.startsWith("/analytics")
      ) {
        const isTherapy = notification.type === "therapy";

        const payload = isTherapy
          ? { redirectTo }
          : {
              redirectTo,
              isWorkPersonality: !!notification.isWorkPersonality,
            };

        const eventName = isTherapy
          ? "ta-therapist-notification"
          : "ta-test-notification";
        const storageKey = isTherapy
          ? "pendingTherapistNotification"
          : "pendingTestNotification";

        if (code === "WAP") {
          // Bell already on the right page — no navigation, just broadcast.
          window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
        } else {
          sessionStorage.setItem(storageKey, JSON.stringify(payload));
          navigate("/analytics");
        }
      } else {
        // grievance — unchanged
        sessionStorage.setItem("pendingHighlightGrievance", true);
        navigate("/grievances");
      }
    }

    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    markAllAsReadApi(code);
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setIsOpen((prev) => !prev)}
        type="button"
        className="relative flex items-center justify-center w-10 h-10 rounded-full hover:scale-110 transition"
        aria-label="Notifications"
      >
        <Bell size={22} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile backdrop — panel becomes a bottom sheet under sm breakpoint */}
          <div
            className="fixed inset-0 z-40 bg-black/30 sm:hidden"
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={panelRef}
            className="fixed sm:absolute z-50 bottom-0 left-0 right-0 sm:bottom-auto sm:left-auto sm:right-0 sm:top-12
              w-full sm:w-96 max-h-[75vh] sm:max-h-[28rem] flex flex-col
              bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
              rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-xs font-medium hover:opacity-70"
                  style={{ color: BRAND.purple }}
                >
                  <Check size={13} /> Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  Loading…
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  You're all caught up.
                </div>
              ) : (
                notifications.map((n) => {
                  const meta = TYPE_META[n.type] || TYPE_META.activity;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleItemClick(n.id)}
                      className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 dark:border-slate-800/60
                        transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50
                        ${n.read ? "opacity-60" : "bg-slate-50/60 dark:bg-slate-800/30"}`}
                    >
                      <div className="relative shrink-0 mt-0.5">
                        <div
                          className="flex items-center justify-center w-8 h-8 rounded-full"
                          style={{ background: `${meta.color}1A` }}
                        >
                          <Icon size={15} style={{ color: meta.color }} />
                        </div>
                        {!n.read && (
                          <span
                            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-900"
                            style={{ background: BRAND.pink }}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-sm ${n.read ? "font-normal" : "font-semibold"}`}
                        >
                          {n.title}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                          {n.message}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1">
                          {timeAgo(n.createdAt)}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
